import { CommonModule, DatePipe } from '@angular/common'
import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core'
import { MatTableModule } from '@angular/material/table'
import { Router, RouterLink } from '@angular/router'
import { Title } from '@angular/platform-browser'

import { InfiniteScrollDirective } from 'ngx-infinite-scroll'
import { finalize, of, tap } from 'rxjs'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'

import { ButtonComponent } from '../../components/button/button.component'
import { LoadingComponent } from '../../components/loading/loading.component'
import { SearchInputComponent } from '../../components/search-input/search-input.component'
import {
  Option,
  SelectComponent,
} from '../../components/select/select.component'
import { User } from '../../services/user/user.mapping'
import { UserStore } from '../../stores/user.store'
import { getTitle } from '../../util/accessibility'
import { isFeatureAllowed } from '../../config/feature-permission.config'

interface UserRow {
  id: string
  name: string
  department: string
  latestBusinessMeetingName: string
  latestBusinessMeetingDate: Date | null
  roleplayCount: number
}

@UntilDestroy()
@Component({
  selector: 'app-user-list-page',
  standalone: true,
  imports: [
    CommonModule,
    ButtonComponent,
    MatTableModule,
    DatePipe,
    SearchInputComponent,
    InfiniteScrollDirective,
    LoadingComponent,
    SelectComponent,
    RouterLink,
  ],
  templateUrl: './user-list-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './user-list-page.component.scss',
})
export class UserListPageComponent implements OnInit {
  @ViewChild('tableContainer') tableContainer: ElementRef | undefined

  readonly PAGE_SIZE_LIMIT = 100

  displayedColumns: string[] = [
    'name',
    'department',
    'latestBusinessMeetingName',
    'latestBusinessMeetingDate',
    'dashboardLink',
  ]
  dataSource: UserRow[] = []

  selectableDepartments: Option[] = []
  selectedDepartmentNames: string[] = []

  monthOptions: Option[] = []
  selectedMonth: Option = { id: '', name: '' }

  loading = true
  isRoleplayEnabled = false

  sortField = 'name'
  sortDirection: 'asc' | 'desc' = 'asc'

  offset = 0

  queryName$ = of(null)
  queryName = ''

  constructor(
    private router: Router,
    private userStore: UserStore,
    private titleService: Title,
  ) {
    this.titleService.setTitle(getTitle('メンバー一覧'))
  }

  ngOnInit() {
    this.userStore
      .getOneSelf({ useCache: true })
      .pipe(untilDestroyed(this))
      .subscribe((user) => {
        this.isRoleplayEnabled = isFeatureAllowed(
          'ROLEPLAY',
          user.enabledFeatures,
        )

        const columns = [
          'name',
          'department',
          'latestBusinessMeetingName',
          'latestBusinessMeetingDate',
        ]
        if (this.isRoleplayEnabled) {
          columns.push('roleplayCount')
          this.initializeMonthOptions()
        }
        columns.push('dashboardLink')
        this.displayedColumns = columns

        this.loadUsers()
      })

    this.userStore
      .getDepartmentNames()
      .pipe(untilDestroyed(this))
      .subscribe((departmentNames) => {
        this.selectableDepartments = [
          {
            id: '',
            name: '全ての部署',
            isDefault: true,
          },
          ...departmentNames.map((name) => ({
            id: name,
            name,
          })),
        ]
      })
  }

  private initializeMonthOptions(): void {
    const now = new Date()
    const currentYear = now.getFullYear()
    const currentMonth = now.getMonth() + 1

    const months: Option[] = []

    for (let i = 0; i < 12; i++) {
      let year = currentYear
      let month = currentMonth - i

      if (month <= 0) {
        month += 12
        year -= 1
      }

      const yearMonth = `${year}${month.toString().padStart(2, '0')}`
      months.push({
        id: yearMonth,
        name: yearMonth,
        isDefault: i === 0,
      })
    }

    this.monthOptions = months
    this.selectedMonth = months[0]
  }

  monthSelected(option: Option): void {
    if (option) {
      this.selectedMonth = option
      this.offset = 0
      this.loadUsers()
    }
  }

  navigateToUserDetail(userId: string) {
    this.router.navigate(['/home'], { queryParams: { 'user-id': userId } })
  }

  queryNameChange(queryName: string) {
    this.offset = 0
    this.queryName = queryName
    this.loadUsers()
  }

  sort(sortField: string) {
    this.loading = true
    if (this.sortField === sortField) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
    } else {
      this.sortDirection = 'asc'
      this.sortField = sortField
    }

    this.offset = 0
    this.loadUsers()
  }

  departmentSelected(option: Option) {
    if (option) {
      this.offset = 0
      this.selectedDepartmentNames = option.id ? [option.id] : []
      this.loadUsers()
    }
  }

  async loadMoreUsers() {
    this.loading = true
    this.offset += 1
    this.loadUsers()
  }

  private scrollTop() {
    if (this.tableContainer?.nativeElement?.scrollTop) {
      this.tableContainer.nativeElement.scrollTop = 0
    }
  }

  private toUserRow(user: User): UserRow {
    const latestBusinessMeeting = user.latestAssessment?.businessMeeting

    return {
      id: user.id,
      name: user.name,
      department: user.departmentName,
      latestBusinessMeetingName: latestBusinessMeeting?.title || '',
      latestBusinessMeetingDate: latestBusinessMeeting?.recordDate || null,
      roleplayCount: user.roleplayCount,
    }
  }

  private loadUsers(): void {
    this.loading = true
    this.userStore
      .searchUsers(
        this.queryName ? this.queryName : null,
        this.selectedDepartmentNames,
        {
          limit: this.PAGE_SIZE_LIMIT,
          offset: this.PAGE_SIZE_LIMIT * this.offset,
        },
        {
          sortField: this.sortField,
          sortOrder: this.sortDirection,
        },
        false,
        this.isRoleplayEnabled && this.selectedMonth.id
          ? this.selectedMonth.id
          : undefined,
      )
      .pipe(
        tap((users) => {
          if (this.offset === 0) {
            this.dataSource = []
            this.scrollTop()
          }
          this.dataSource = this.dataSource.concat(users.map(this.toUserRow))
        }),
        finalize(() => (this.loading = false)),
      )
      .subscribe()
  }
}
